import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./Welcome.scss";

gsap.registerPlugin(ScrollTrigger);

// ✅ Webpack-compatible dynamic import using require.context
// Automatically picks up all .jpg files in the folder — no manual imports needed
const importAll = (requireContext) =>
  requireContext.keys().map(requireContext);

const allImages = importAll(
  require.context(
    "../../assets/images/photoSection",
    false,
    /\.(jpg|jpeg)$/
  )
);

// Sort numerically: 1.jpg, 2.jpg ... 39.jpg
const originalImages = [...allImages].sort((a, b) => {
  const numA = parseInt(a.match(/(\d+)\.(jpg|jpeg)/)?.[1] || "0");
  const numB = parseInt(b.match(/(\d+)\.(jpg|jpeg)/)?.[1] || "0");
  return numA - numB;
});

// Fisher-Yates shuffle
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// First few images load eagerly (visible when section enters viewport)
const EAGER_COUNT = 6;

const Welcome = () => {
  const gridRef = useRef(null);
  const imageContainerRefs = useRef([]);

  // ✅ Lazy initializer — shuffle runs only once on first render
  const [imagesToShow, setImagesToShow] = useState(() =>
    window.innerWidth <= 768
      ? shuffleArray(originalImages).slice(0, 35)
      : shuffleArray(originalImages).slice(0,39)
  );

  // ✅ Debounced resize — prevents re-shuffle on every pixel of resize
  const updateImages = useCallback(() => {
    if (window.innerWidth <= 768) {
      setImagesToShow(shuffleArray(originalImages).slice(0, 35));
    } else {
      setImagesToShow(shuffleArray(originalImages).slice(0,39));
    }
  }, []);

  useEffect(() => {
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateImages, 300);
    };
    window.addEventListener("resize", debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", debouncedResize);
    };
  }, [updateImages]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const validRefs = imageContainerRefs.current.filter(Boolean);

    gsap.set(validRefs, {
      scale: 0.8,
      opacity: 0,
      y: 50,
      willChange: "transform, opacity",
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: grid,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse",
      },
      onComplete: () => {
        // ✅ Free GPU memory after animation ends
        validRefs.forEach((el) => {
          if (el) el.style.willChange = "auto";
        });
      },
    });

    tl.to(validRefs, {
      scale: 1,
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.03,
      ease: "power3.out",
    });

    return () => {
      if (tl.scrollTrigger) tl.scrollTrigger.kill();
      tl.kill();
    };
  }, [imagesToShow]);

  return (
    <div>
      <section className="welcome">
        <div className="content" data-aos="fade-right">
          <h1>
            Welcome <br />
            <span
              style={{
                fontFamily: "Playfair, serif",
                fontStyle: "italic",
                fontSize: "90px",
              }}
            >
              to
            </span>
            <span className="luster-text">Luster</span>
          </h1>
          <p data-aos="fade-left">
            Where every event becomes unforgettable. Explore our premium photo
            booth experiences, customized prints, and interactive services that
            create fun and lasting memories.
          </p>
        </div>
      </section>

      <section className="getPhoto" ref={gridRef}>
        {imagesToShow.map((src, index) => (
          <div
            key={index}
            ref={(el) => (imageContainerRefs.current[index] = el)}
            className="photoBox"
            onContextMenu={(e) => e.preventDefault()}
          >
            <img
              src={src}
              alt={`gallery-${index + 1}`}
              draggable="false"
              loading={index < EAGER_COUNT ? "eager" : "lazy"}
              decoding={index < EAGER_COUNT ? "sync" : "async"}
              fetchpriority={index < EAGER_COUNT ? "high" : "low"}
            />
          </div>
        ))}

        <Link className="overlay-text" to="/Getphoto" style={{
          fontFamily: "Playfair, serif",
          fontStyle: "italic",
          fontSize: "90px",
        }}>
          Get Your <span style={{ color: "#76cfff" }}>Photo</span> Now
        </Link>
      </section>
    </div>
  );
};

export default Welcome;